import Header from "../components/Header";
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from "react-router-dom";
import {
    Box, Breadcrumbs, Button, Card, Chip, IconButton, InputAdornment, Link, Skeleton, Switch, Table, TableBody,
    TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import { FormatDate } from "../../utils/FormatDate";
import EditarDesconto from "./components/EditarDesconto";
import AdicionarDesconto from "./components/AdicionarDesconto";

const corPrimaria = "#0b243d";

const filtros = [
    { chave: "todos", label: "Todos" },
    { chave: "ativos", label: "Ativos" },
    { chave: "inativos", label: "Inativos" },
];

const textoMeses = (meses) => (Number(meses) === 1 ? "1 mês" : `${meses} meses`);

function Descontos() {

    const token = localStorage.getItem('token');
    const navigate = useNavigate();
    const [descontos, setDescontos] = useState([]);
    const [carregando, setCarregando] = useState(true);
    const [editarDesconto, setEditarDesconto] = useState(false);
    const [adicionarDesconto, setAdicionarDesconto] = useState(false);
    const [idDesconto, setIdDesconto] = useState(null);
    const [alterandoStatus, setAlterandoStatus] = useState(null); // idDesconto em andamento
    const [codigoCopiado, setCodigoCopiado] = useState(null);
    const [busca, setBusca] = useState("");
    const [filtro, setFiltro] = useState("todos");

    useEffect(() => {
        const getDescontos = async () => {
            try {
                const response = await axios.get(`${process.env.REACT_APP_API_URL}/listarDescontos`, {
                    headers: {
                        Authorization: token
                    }
                });
                setDescontos(response.data.descontos || []);
                console.log(response.data.descontos);
            } catch (err) {
                console.error(err);
            } finally {
                setCarregando(false);
            }
        };
        getDescontos();
    }, [token, editarDesconto, adicionarDesconto]);

    const handleOpenEditarDesconto = (idDesconto) => {
        setIdDesconto(idDesconto);
        setEditarDesconto(true);
    }

    // Em caso de erro o back não manda a lista; mantém a atual para a tela não quebrar
    const handleDesabilitarDesconto = async (idDesconto) => {
        setAlterandoStatus(idDesconto);
        try {
            const response = await axios.put(`${process.env.REACT_APP_API_URL}/desabilitarDesconto/${idDesconto}`, {}, {
                headers: {
                    Authorization: token
                }
            });
            if (response.data.descontos) setDescontos(response.data.descontos);
            else alert(response.data.erro || "Não foi possível desabilitar o desconto.");
        } finally {
            setAlterandoStatus(null);
        }
    }

    const handleHabilitarDesconto = async (idDesconto) => {
        setAlterandoStatus(idDesconto);
        try {
            const response = await axios.put(`${process.env.REACT_APP_API_URL}/habilitarDesconto/${idDesconto}`, {}, {
                headers: {
                    Authorization: token
                }
            });
            if (response.data.descontos) setDescontos(response.data.descontos);
            else alert(response.data.erro || "Não foi possível habilitar o desconto.");
        } finally {
            setAlterandoStatus(null);
        }
    }

    const copiarCodigo = async (codigo) => {
        try {
            await navigator.clipboard.writeText(codigo);
            setCodigoCopiado(codigo);
            setTimeout(() => setCodigoCopiado(null), 1500);
        } catch (err) {
            console.error("Não foi possível copiar o código:", err);
        }
    };

    const contagem = {
        todos: descontos.length,
        ativos: descontos.filter((d) => d.status).length,
        inativos: descontos.filter((d) => !d.status).length,
    };

    const descontosFiltrados = useMemo(() => {
        const termo = busca.trim().toLowerCase();
        return descontos
            .filter((d) => filtro === "todos" || (filtro === "ativos" ? d.status : !d.status))
            .filter((d) => !termo || String(d.discountCode || "").toLowerCase().includes(termo));
    }, [descontos, busca, filtro]);


    return (
        <div>
            <Header />
            <Box sx={{ width: "100%", display: "flex", justifyContent: "center", pb: 6 }}>
                <Box sx={{ width: "90%", maxWidth: 1280, mt: 2.5 }}>
                    <Breadcrumbs aria-label="breadcrumb">
                        <Link underline="hover" color="inherit" href="../Dashboard">
                            Dashboard
                        </Link>
                        <Typography sx={{ color: 'text.primary' }}>Descontos</Typography>
                    </Breadcrumbs>

                    <Box sx={{ mt: 2, mb: 3, display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 2 }}>
                        <Box>
                            <Typography sx={{ fontSize: 24, fontWeight: 600 }}>Descontos</Typography>
                            <Typography variant="body2" color="text.secondary">
                                Códigos promocionais que os clientes usam ao assinar um plano.
                            </Typography>
                        </Box>
                        <Button
                            variant="contained"
                            size="small"
                            startIcon={<AddIcon />}
                            sx={{ backgroundColor: corPrimaria, textTransform: "none", "&:hover": { backgroundColor: "#153d66" } }}
                            onClick={() => setAdicionarDesconto(true)}
                        >
                            Adicionar Desconto
                        </Button>
                    </Box>

                    <Card variant="outlined" sx={{ borderRadius: 2 }}>
                        {/* Busca e filtros */}
                        <Box sx={{ p: 2.5, display: "flex", flexWrap: "wrap", alignItems: "center", gap: 2 }}>
                            <TextField
                                size="small"
                                placeholder="Buscar pelo código"
                                value={busca}
                                onChange={(e) => setBusca(e.target.value)}
                                sx={{ flex: "1 1 280px" }}
                                slotProps={{
                                    input: {
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon fontSize="small" />
                                            </InputAdornment>
                                        ),
                                    },
                                }}
                            />
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                                {filtros.map((item) => {
                                    const ativo = filtro === item.chave;
                                    return (
                                        <Chip
                                            key={item.chave}
                                            label={`${item.label} (${contagem[item.chave]})`}
                                            size="small"
                                            variant={ativo ? "filled" : "outlined"}
                                            onClick={() => setFiltro(item.chave)}
                                            sx={ativo ? { backgroundColor: corPrimaria, color: "#fff", "&:hover": { backgroundColor: "#153d66" } } : undefined}
                                        />
                                    );
                                })}
                            </Box>
                        </Box>

                        <TableContainer sx={{ borderTop: 1, borderColor: "divider" }}>
                            <Table size="small" sx={{ minWidth: 760 }}>
                                <TableHead>
                                    <TableRow sx={{ backgroundColor: "#fafafa", "& th": { fontWeight: 600, color: "text.secondary", fontSize: 13, py: 1.5 } }}>
                                        <TableCell>Código</TableCell>
                                        <TableCell>Desconto</TableCell>
                                        <TableCell>Duração</TableCell>
                                        <TableCell>Última alteração</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell align="right">Ações</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {carregando ? (
                                        Array.from({ length: 4 }).map((_, i) => (
                                            <TableRow key={i}>
                                                {Array.from({ length: 6 }).map((__, j) => (
                                                    <TableCell key={j} sx={{ py: 2 }}><Skeleton /></TableCell>
                                                ))}
                                            </TableRow>
                                        ))
                                    ) : descontosFiltrados.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={6} sx={{ py: 6, textAlign: "center", borderBottom: 0 }}>
                                                <LocalOfferOutlinedIcon sx={{ fontSize: 40, color: "text.disabled" }} />
                                                <Typography sx={{ fontWeight: 500, mt: 1 }}>Nenhum desconto encontrado</Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    {busca || filtro !== "todos" ? "Tente outro código ou filtro." : "Crie o primeiro código em \"Adicionar Desconto\"."}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        descontosFiltrados.map((desconto) => (
                                            <TableRow key={desconto?.idDesconto} hover sx={{ "& td": { py: 1.25 }, opacity: desconto.status ? 1 : 0.65 }}>
                                                <TableCell>
                                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                                        <Box
                                                            component="span"
                                                            sx={{ fontFamily: "monospace", fontWeight: 600, fontSize: 14, px: 1, py: 0.25, borderRadius: 1, backgroundColor: "#f0f3f7", color: corPrimaria }}
                                                        >
                                                            {desconto?.discountCode}
                                                        </Box>
                                                        <Tooltip title={codigoCopiado === desconto?.discountCode ? "Copiado!" : "Copiar código"}>
                                                            <IconButton size="small" aria-label="Copiar código" onClick={() => copiarCodigo(desconto?.discountCode)}>
                                                                <ContentCopyIcon sx={{ fontSize: 16 }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Box>
                                                    <Typography variant="caption" color="text.secondary">#{desconto?.idDesconto}</Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography sx={{ fontWeight: 700, color: "success.main" }}>{desconto?.valorDesconto}%</Typography>
                                                </TableCell>
                                                <TableCell>{textoMeses(desconto?.duracaoMeses)}</TableCell>
                                                <TableCell sx={{ whiteSpace: "nowrap" }}>{desconto?.updatedAt ? FormatDate(desconto.updatedAt) : "—"}</TableCell>
                                                <TableCell>
                                                    <Box sx={{ display: "flex", alignItems: "center" }}>
                                                        <Tooltip title={desconto.status ? "Desabilitar" : "Habilitar"}>
                                                            <Switch
                                                                size="small"
                                                                color="success"
                                                                checked={!!desconto.status}
                                                                disabled={alterandoStatus === desconto.idDesconto}
                                                                onChange={() => (desconto.status
                                                                    ? handleDesabilitarDesconto(desconto.idDesconto)
                                                                    : handleHabilitarDesconto(desconto.idDesconto))}
                                                            />
                                                        </Tooltip>
                                                        <Typography variant="body2" sx={{ color: desconto.status ? "success.main" : "text.secondary", fontWeight: 500 }}>
                                                            {desconto.status ? "Ativo" : "Inativo"}
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                                <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                                                    <Button
                                                        size="small"
                                                        startIcon={<PeopleOutlineIcon fontSize="small" />}
                                                        sx={{ textTransform: "none", color: corPrimaria, mr: 0.5 }}
                                                        onClick={() => navigate(`../DescontosUsados/${desconto.idDesconto}`)}
                                                    >
                                                        Usado por
                                                    </Button>
                                                    <Tooltip title="Editar">
                                                        <IconButton size="small" aria-label="Editar desconto" onClick={() => handleOpenEditarDesconto(desconto.idDesconto)}>
                                                            <EditOutlinedIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Card>
                </Box>
            </Box>
            <EditarDesconto editarDesconto={editarDesconto} setEditarDesconto={setEditarDesconto} idDesconto={idDesconto} />
            <AdicionarDesconto adicionarDesconto={adicionarDesconto} setAdicionarDesconto={setAdicionarDesconto} idDesconto={idDesconto} />
        </div>
    );
}

export default Descontos;
